import { useState } from 'react';
import { font } from 'asserts/styles/fonts';

import { color } from 'asserts/styles/colors';
import styled from 'styled-components';
import RoundCheckbox from 'components/checkbox/Checkbox';
import DefaultButton from 'components/button/DefaultButton';

import { ReactComponent as IconCheck } from 'asserts/icons/ic_chevron_check_blue.svg';
import Pay from 'components/terms/Pay';
import useCalculateTotalPriceForAllMemberships from 'hooks/useCalculateTotalPriceForAllMemberships';
import { Modal } from 'antd';
import { useAtomValue } from 'jotai';
import userInforAtom from 'atom/userInfoAtom';
import companyInforAtom from 'atom/companyInfoAtom';
import useCalculateMembershipTotalPrice from 'hooks/useCalculateMembershipTotalPrice';
import CardInputModal from './CardInputModal';

interface SubscribeProps {
  open: boolean;
  cartData: MemberShipDataTypes[];
  onCancel: () => void;
}

type MembershipDataToParams = {
  count: number;
  info: {
    ebticketidx: number;
    division: number[];
    price: string;
    subscribetype: number;
    subscribeid: string;
    licenseidx: number;
  };
};

export type CardType = 'corporation' | 'individual';

const SubScribeModal = ({ open, cartData, onCancel }: SubscribeProps) => {
  const [isSelected, setIsSelected] = useState<boolean>(false);
  const [openCardInputModal, setOpenCardInputModal] = useState(false);
  const [selectedCard, setSelectedCard] = useState<CardType>('individual');

  const paymentType = selectedCard === 'individual' ? 300 : 200; // 100: 페이팔 200: 토스 300: 나이스페이

  const totalPriceForAllMemberships = useCalculateTotalPriceForAllMemberships(cartData);

  const userInfor = useAtomValue(userInforAtom);
  const companyInfor = useAtomValue(companyInforAtom);

  const handleClickOpenCardInputModal = () => {
    setOpenCardInputModal(true);
  };

  const handleClickCloseCardInputModal = () => {
    setOpenCardInputModal(false);
  };

  const handleCheckBoxChange = (e: any) => {
    setIsSelected((prev) => !prev);
  };

  const calculateMembershipTotalPrice = useCalculateMembershipTotalPrice();

  const selectedCardData: MembershipDataToParams[] = cartData.map((item: MemberShipDataTypes) => ({
    count: item.count,
    info: {
      ebticketidx: item.ebticketidx,
      division: item.divisiondata
        .filter((division) => !division.hasOwnProperty('recommendyn') || division.recommendyn)
        .map((division) => division.division),
      price: calculateMembershipTotalPrice(item).toString(),
      subscribetype: item.subscribetype,
      subscribeid: '',
      licenseidx: item.licenseData?.licenseidx || 0
    }
  }));

  // 결제 시 전달해야할 결제정보
  const paymentData = {
    uid: userInfor.uid,
    usridx: userInfor.usridx,
    companyidx: companyInfor.companyidx,
    centeridx: companyInfor.centeridx,
    companyname: companyInfor.companyname,
    countrycode: 'KR',
    termidx: 10,
    agree: 1,
    currency: 'KRW',
    successurl: '',
    cancelurl: '',
    failedurl: '',
    paymenttype: paymentType,
    paymentmethod: 1, // 무료 0, 신용카드 1, 충전 2
    membershipdata: selectedCardData
  };

  return (
    <>
      <Modal open={open} onCancel={onCancel} footer={null} centered width={640}>
        <ModalWrapper>
          <Title>비즈멤버십 구독</Title>
          <SubTitle>
            <IconCheck />
            선택한 수량만큼 이용가능합니다.
          </SubTitle>
          <PriceInfoWrapper>
            <h5>멤버십</h5>
            <div>
              {cartData.map((data) => {
                const { ticketnamecustomer, divisiondata, editable, uuid } = data;

                const selectedDataLength = divisiondata.length;

                let recommendCount = 0;

                if (selectedDataLength !== 0) {
                  divisiondata.forEach((item) => {
                    if (item.hasOwnProperty('recommendyn') && item.recommendyn) {
                      recommendCount++;
                    }
                  });
                }

                return (
                  <PriceInfo key={uuid}>
                    <h6>{ticketnamecustomer}</h6>
                    <SelectedDataCount>
                      {/*<div>기본이용권</div>*/}
                      <div>이용권</div>
                      {selectedDataLength !== 0 && (
                        <div>
                          &nbsp;+&nbsp;검사이용권
                          {editable ? recommendCount : selectedDataLength}/{selectedDataLength}
                        </div>
                      )}
                    </SelectedDataCount>
                  </PriceInfo>
                );
              })}
            </div>
          </PriceInfoWrapper>
          <TotalPriceWrapper>
            <h5>총 금액</h5>
            <h5>{totalPriceForAllMemberships.toLocaleString()}원</h5>
          </TotalPriceWrapper>

          <TermsWrapper>
            <TermsTitle>엑스바디 비즈멤버십 결제 약관에 동의하여 주세요.</TermsTitle>
            <TermsContents className="scrollbar">
              <Pay />
            </TermsContents>
            <RadioButtonWrapper>
              <RoundCheckbox
                name="bizTerms"
                label="약관에 동의합니다."
                onChange={handleCheckBoxChange}
                checked={isSelected}
              />
            </RadioButtonWrapper>
          </TermsWrapper>
          <ButtonWrapper $isSelected={isSelected}>
            <DefaultButton text="닫기" onClick={onCancel} />
            <DefaultButton
              type="primary"
              text="구독"
              onClick={handleClickOpenCardInputModal}
              disabled={!isSelected}
            />
          </ButtonWrapper>
        </ModalWrapper>
        {openCardInputModal && (
          <CardInputModal
            selectedCard={selectedCard}
            setSelectedCard={setSelectedCard}
            paymentData={paymentData}
            open={openCardInputModal}
            onCancel={handleClickCloseCardInputModal}
          />
        )}
      </Modal>
    </>
  );
};

export default SubScribeModal;

const ModalWrapper = styled.div`
  flex-direction: column;
  justify-content: center;
  align-items: center;

  h5 {
    width: 120px;
    ${font.h4_bold};
  }
`;

const Title = styled.h2`
  ${font.h4_medium};
  text-align: center;
`;

export const SubTitle = styled.p`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 16px;
  border-radius: 24px;
  border: 1px solid ${color.blue500};
  color: ${color.blue500};
  ${font.body_medium};
  margin: 32px 0 24px;
`;

const PriceInfoWrapper = styled.div`
  display: flex;
  padding: 16px 32px;
  align-items: center;
  gap: 32px;
  width: 100%;
  border-radius: 12px;
  background: ${color.gray100};

  & > div {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  h6 {
    ${font.body_medium};
  }

  p {
    ${font.body_regular};
  }
`;

const PriceInfo = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

const TotalPriceWrapper = styled.div`
  display: flex;
  padding: 16px 32px;
  align-items: center;
  gap: 32px;
  width: 100%;
  border-radius: 12px;
  background: ${color.blue50};
  margin: 8px 0 24px;
`;

const TermsWrapper = styled.div`
  display: flex;
  min-height: 240px;
  padding: 16px;
  flex-direction: column;
  gap: 16px;
  border-radius: 8px;
  border: 1px solid ${color.opacityBlack01};
`;

const TermsTitle = styled.p`
  font-weight: 500;
  line-height: 20px;
`;

const TermsContents = styled.div`
  width: 100%;
  color: ${color.gray700};
  ${font.caption1_regular};
`;

const RadioButtonWrapper = styled.div`
  display: flex;
  align-self: flex-end;
  .ant-checkbox-wrapper .ant-checkbox .ant-checkbox-inner {
    width: 18px;
    height: 18px;
    &:after {
      top: 8px;
      left: 5px;
      width: 6px;
      height: 9px;
    }
  }
`;

const ButtonWrapper = styled.div<{
  $isSelected: boolean;
}>`
  display: flex;
  gap: 12px;
  margin-top: 32px;
  justify-content: center;

  button {
    width: 160px;
    height: 36px;
  }

  p:first-of-type button {
    background-color: ${color.white};
    border: 1px solid ${color.gray400} !important;
    color: ${color.gray500} !important;
  }
`;

const SelectedDataCount = styled.div`
  display: flex;
`;
